/**
 * useHelpDeskSubmit Composable
 *
 * Provides a unified submit handler for the HelpDeskDialog component that
 * works for both logged-in and anonymous (not-logged-in) users.
 *
 * When the user is logged-in the handler appends `hospitalId` from config;
 * for anonymous users it skips that field.  The `isLoggedIn` state is
 * determined by `authStore.isAuthenticated` at call time so a single
 * function reference works regardless of where it is used.
 */
import type { HelpDeskTicketPayload } from '@tms-phoenix/ui-vue';
import { useToaster } from '@tms-phoenix/ui-vue';
import { isAxiosError } from 'axios';
import { useLocale } from '@/shared/composables/useLocale';
import { useAuthStore } from '@/stores';
import { getApiClient } from '@/core/services/api.service';
import { logAuditEvent } from '@/core/services/audit.service';
import { API_ROUTES, config } from '@/core/config';

/**
 * Returns an async handler compatible with `useHelpDesk(onSubmit)`.
 *
 * Usage:
 * ```ts
 * const submitTicket = useHelpDeskSubmit();
 * const helpDesk    = useHelpDesk(submitTicket);
 * ```
 */
export function useHelpDeskSubmit(): (payload: HelpDeskTicketPayload) => Promise<void> {
  const { t } = useLocale();
  const authStore = useAuthStore();
  const toast = useToaster();

  return async (payload: HelpDeskTicketPayload): Promise<void> => {
    const api = getApiClient();
    const isLoggedIn = authStore.isAuthenticated;

    // Build multipart/form-data so the actual screenshot file is uploaded
    const formData = new FormData();
    formData.append('name', payload.userName);
    formData.append('email', payload.email);
    formData.append('currentPath', payload.currentPath);
    formData.append('issueType', payload.issueType);
    formData.append('subject', payload.subject);
    formData.append('description', payload.description);
    if (payload.attachment) {
      formData.append('attachment', payload.attachment, payload.attachment.name);
    }

    // Attach hospitalId when available (logged-in users only).
    // The field is optional — the backend accepts tickets without it.
    if (isLoggedIn && config.hospitalId) {
      formData.append('hospitalId', config.hospitalId);
    }

    let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
    let auditErrorCode: string | undefined;
    let auditErrorMessage: string | undefined;

    try {
      await api.post(`${API_ROUTES.SETTINGS}/help-desk-tickets`, formData);
      auditStatus = 'SUCCESS';
      toast.success(t('helpDesk.submitSuccess'));
    } catch (err) {
      auditErrorCode = 'HELPDESK_SUBMIT_FAILED';
      auditErrorMessage = err instanceof Error ? err.message : t('helpDesk.submitFailed');
      if (isAxiosError(err)) {
        const apiErr = err.response?.data?.error;
        auditErrorCode = typeof apiErr === 'string' ? apiErr : err.code ?? auditErrorCode;
      }
      toast.danger(t('helpDesk.submissionFailed'), { detail: auditErrorMessage });
      // Re-throw so useHelpDesk keeps the dialog open for retry
      throw err;
    } finally {
      try {
        logAuditEvent({
          eventCategory: 'SETTINGS',
          eventType: 'CREATE',
          eventStatus: auditStatus,
          resourceType: 'helpDeskTickets',
          isPhiAccess: false,
          newValue: {
            issueType: payload.issueType,
            subject: payload.subject,
            hasAttachment: !!payload.attachment,
          },
          errorCode: auditErrorCode,
          errorMessage: auditErrorMessage,
        });
      } catch { /* Audit must never block the flow */ }
    }
  };
}
